import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KpiCard from '../components/KpiCard';
import RangeBar from '../components/RangeBar';
import TechInsightsDrawer from '../components/TechInsightsDrawer';
import {
  getIndustrialRiskFragility,
  getIndustrialRiskGlobalDistribution,
  getIndustrialRiskOverview,
  getIndustrialRiskRiskDrivers,
} from '../services/industrialRiskService';

function eurKg(n: number) {
  return n.toFixed(2);
}
function pct01(p: number) {
  return (p * 100).toFixed(1);
}

type ControlState = 'SEGURO' | 'TENSION' | 'FRAGIL' | 'REVISAR_DATOS';

function computeControlState(args: {
  decisionGrade?: boolean;
  probLoss?: number;
  buffer?: number; // mean - adverse
}): { state: ControlState; rationale: string } {
  const { decisionGrade, probLoss, buffer } = args;

  if (decisionGrade === false) {
    return { state: 'REVISAR_DATOS', rationale: 'La calidad de datos no es suficiente para decisión ejecutiva.' };
  }
  if (probLoss == null || buffer == null) {
    return { state: 'REVISAR_DATOS', rationale: 'Faltan métricas clave para evaluar control.' };
  }

  // Reglas conservadoras, fáciles de defender:
  // - SEGURO: pérdida rara + colchón razonable
  // - TENSIÓN: riesgo bajo-medio o colchón estrecho
  // - FRÁGIL: riesgo alto o sin colchón
  if (probLoss <= 0.05 && buffer >= 0.25) {
    return { state: 'SEGURO', rationale: 'Riesgo bajo y colchón suficiente frente a escenarios adversos.' };
  }
  if (probLoss <= 0.20 && buffer > 0.10) {
    return { state: 'TENSION', rationale: 'El negocio sigue en control, pero el colchón es limitado o el riesgo no es trivial.' };
  }
  return { state: 'FRAGIL', rationale: 'Pequeños cambios pueden empujar el negocio a zona de riesgo.' };
}

function stateColor(state: ControlState): 'success' | 'warning' | 'error' | 'info' {
  if (state === 'SEGURO') return 'success';
  if (state === 'TENSION') return 'warning';
  if (state === 'FRAGIL') return 'error';
  return 'info';
}

type GlobalDistExtract = {
  global?: {
    summary?: { mean?: number; p10?: number; p90?: number; probability_of_loss?: number; min?: number; max?: number };
    histogram?: { bins?: number[]; counts?: number[] };
  };
  suppliers?: { supplier: string; baseline_price_eur_per_kg?: number; summary?: { mean?: number; p10?: number; p90?: number } }[];
  quality?: { decision_grade?: boolean; reasons?: string[] };
};

function extractGlobalDist(data: any): GlobalDistExtract {
  const g = data?.global;
  const qr = data?.quality_report;

  const global =
    g?.summary && g?.histogram
      ? {
          summary: {
            mean: g.summary.mean,
            p10: g.summary.p10,
            p90: g.summary.p90,
            probability_of_loss: g.summary.probability_of_loss,
            min: g.summary.min,
            max: g.summary.max,
          },
          histogram: {
            bins: Array.isArray(g.histogram.bins) ? g.histogram.bins.map((x: any) => Number(x)) : undefined,
            counts: Array.isArray(g.histogram.counts) ? g.histogram.counts.map((x: any) => Number(x)) : undefined,
          },
        }
      : undefined;

  const suppliers = Array.isArray(data?.suppliers)
    ? data.suppliers
        .map((s: any) => ({
          supplier: String(s?.supplier ?? ''),
          baseline_price_eur_per_kg: typeof s?.baseline_price_eur_per_kg === 'number' ? s.baseline_price_eur_per_kg : undefined,
          summary:
            s?.summary && typeof s.summary.mean === 'number'
              ? { mean: s.summary.mean, p10: s.summary.p10, p90: s.summary.p90 }
              : undefined,
        }))
        .filter((s: any) => s.supplier)
    : [];

  const quality = qr
    ? {
        decision_grade: Boolean(qr.decision_grade),
        reasons: Array.isArray(qr.reasons) ? qr.reasons.map((x: any) => String(x)) : [],
      }
    : undefined;

  return { global, suppliers, quality };
}

type OverviewRow = {
  supplier: string;
  total_kg?: number;
  baseline_price?: number;
  verdict?: string;
  mean?: number;
  adverse?: number;
  favorable?: number;
  prob_loss?: number;
  max_price?: number;
  gap_vs_max?: number;
};

type OverviewExtract = {
  global?: { total_kg?: number; n_lots?: number; avg_price_weighted_eur_per_kg?: number };
  rows: OverviewRow[];
};

function extractOverview(data: any): OverviewExtract {
  const g = data?.global;
  const global = g
    ? {
        total_kg: typeof g.total_kg === 'number' ? g.total_kg : undefined,
        n_lots: typeof g.n_lots === 'number' ? g.n_lots : undefined,
        avg_price_weighted_eur_per_kg:
          typeof g.avg_price_weighted_eur_per_kg === 'number' ? g.avg_price_weighted_eur_per_kg : undefined,
      }
    : undefined;

  const rows: OverviewRow[] = Array.isArray(data?.suppliers)
    ? data.suppliers.map((x: any) => {
        const ms = x?.margin_status;
        const pk = ms?.profit_per_kg;
        const mp = x?.max_purchase_price;
        const meta = x?.meta;

        return {
          supplier: String(x?.supplier ?? ''),
          total_kg: typeof meta?.total_kg === 'number' ? meta.total_kg : undefined,
          baseline_price: typeof x?.baseline_price_eur_per_kg === 'number' ? x.baseline_price_eur_per_kg : undefined,
          verdict: typeof ms?.verdict === 'string' ? ms.verdict : undefined,
          mean: typeof pk?.mean === 'number' ? pk.mean : undefined,
          adverse: typeof pk?.adverse === 'number' ? pk.adverse : undefined,
          favorable: typeof pk?.favorable === 'number' ? pk.favorable : undefined,
          prob_loss: typeof pk?.probability_of_loss === 'number' ? pk.probability_of_loss : undefined,
          max_price: typeof mp?.max_price_eur_per_kg === 'number' ? mp.max_price_eur_per_kg : undefined,
          gap_vs_max: typeof mp?.gap_vs_max_price_eur_per_kg === 'number' ? mp.gap_vs_max_price_eur_per_kg : undefined,
        };
      })
    : [];

  return { global, rows };
}

type RiskDriversRow = {
  key: string;
  label: string;
  contribution_share?: number; // 0..1
  shapley_effect_es_per_kg?: number; // €/kg (puede ser + o -)
};

type RiskDriversExtract = {
  metric?: { value?: number; alpha?: number };
  drivers: RiskDriversRow[];
  quality?: { decision_grade?: boolean; reasons?: string[] };
};

function extractRiskDrivers(data: any): RiskDriversExtract {
  const m = data?.metric;
  const drivers: RiskDriversRow[] = Array.isArray(data?.drivers)
    ? data.drivers
        .map((d: any) => ({
          key: String(d?.key ?? ''),
          label: String(d?.label ?? ''),
          contribution_share: typeof d?.contribution_share === 'number' ? d.contribution_share : undefined,
          shapley_effect_es_per_kg: typeof d?.shapley_effect_es_per_kg === 'number' ? d.shapley_effect_es_per_kg : undefined,
        }))
        .filter((d: any) => d.key && d.label)
    : [];

  const qr = data?.quality_report;
  const quality = qr
    ? {
        decision_grade: Boolean(qr.decision_grade),
        reasons: Array.isArray(qr.reasons) ? qr.reasons.map((x: any) => String(x)) : [],
      }
    : undefined;

  return {
    metric: m ? { value: typeof m.value === 'number' ? m.value : undefined, alpha: typeof m.alpha === 'number' ? m.alpha : undefined } : undefined,
    drivers,
    quality,
  };
}

type FragilityExtract = {
  quality?: { decision_grade?: boolean; reasons?: string[] };
  baseline_price?: number;

  base?: { mean?: number; p10?: number; es?: number; prob_loss?: number; buffer?: number };

  shockPrice?: {
    delta?: number;
    delta_es?: number;
    delta_buffer?: number;
    delta_prob_loss?: number;
  };

  shockYield?: {
    delta_abs?: number;
    delta_es?: number;
    delta_buffer?: number;
    delta_prob_loss?: number;
  };

  breakpoints?: {
    loss_target?: number;
    purchase_price_delta_to_loss_target?: number | null;
    yield_delta_abs_to_loss_target?: number | null;
  };
};

function extractFragility(data: any): FragilityExtract {
  const qr = data?.quality_report;
  const quality = qr
    ? {
        decision_grade: Boolean(qr.decision_grade),
        reasons: Array.isArray(qr.reasons) ? qr.reasons.map((x: any) => String(x)) : [],
      }
    : undefined;

  const result = data?.result;

  const base = result?.base
    ? {
        mean: typeof result.base.mean === 'number' ? result.base.mean : undefined,
        p10: typeof result.base.p10 === 'number' ? result.base.p10 : undefined,
        es: typeof result.base.es === 'number' ? result.base.es : undefined,
        prob_loss: typeof result.base.prob_loss === 'number' ? result.base.prob_loss : undefined,
        buffer: typeof result.base.buffer === 'number' ? result.base.buffer : undefined,
      }
    : undefined;

  const sp = result?.shocks?.purchase_price_plus;
  const shockPrice = sp
    ? {
        delta: typeof sp.delta_eur_per_kg === 'number' ? sp.delta_eur_per_kg : undefined,
        delta_es: typeof sp.delta_vs_base?.es === 'number' ? sp.delta_vs_base.es : undefined,
        delta_buffer: typeof sp.delta_vs_base?.buffer === 'number' ? sp.delta_vs_base.buffer : undefined,
        delta_prob_loss: typeof sp.delta_vs_base?.prob_loss === 'number' ? sp.delta_vs_base.prob_loss : undefined,
      }
    : undefined;

  const sy = result?.shocks?.yield_minus;
  const shockYield = sy
    ? {
        delta_abs: typeof sy.delta_abs === 'number' ? sy.delta_abs : undefined,
        delta_es: typeof sy.delta_vs_base?.es === 'number' ? sy.delta_vs_base.es : undefined,
        delta_buffer: typeof sy.delta_vs_base?.buffer === 'number' ? sy.delta_vs_base.buffer : undefined,
        delta_prob_loss: typeof sy.delta_vs_base?.prob_loss === 'number' ? sy.delta_vs_base.prob_loss : undefined,
      }
    : undefined;

  const bp = result?.breakpoints;
  const breakpoints = bp
    ? {
        loss_target: typeof bp.loss_target === 'number' ? bp.loss_target : undefined,
        purchase_price_delta_to_loss_target:
          typeof bp.purchase_price_delta_to_loss_target === 'number' ? bp.purchase_price_delta_to_loss_target : bp.purchase_price_delta_to_loss_target ?? null,
        yield_delta_abs_to_loss_target:
          typeof bp.yield_delta_abs_to_loss_target === 'number' ? bp.yield_delta_abs_to_loss_target : bp.yield_delta_abs_to_loss_target ?? null,
      }
    : undefined;

  return {
    quality,
    baseline_price: typeof data?.baseline_price_eur_per_kg === 'number' ? data.baseline_price_eur_per_kg : undefined,
    base,
    shockPrice,
    shockYield,
    breakpoints,
  };
}

function VerdictChip({ verdict }: { verdict?: string }) {
  const v = (verdict ?? '—').toUpperCase();
  const color =
    v === 'ROBUSTO' ? 'success' : v === 'INESTABLE' ? 'warning' : v === 'CRITICO' || v === 'NEGATIVO' ? 'error' : 'default';
  return <Chip size="small" label={v} color={color as any} variant={v === '—' ? 'outlined' : 'filled'} />;
}

function Histogram({
  bins,
  counts,
  minX,
  maxX,
  markers,
}: {
  bins?: number[];
  counts?: number[];
  minX?: number;
  maxX?: number;
  markers: { label: string; x?: number }[];
}) {
  if (!bins?.length || !counts?.length) return null;
  const maxCount = Math.max(...counts, 1);
  const min = typeof minX === 'number' ? minX : bins[0];
  const max = typeof maxX === 'number' ? maxX : bins[bins.length - 1];
  const span = max - min || 1;

  return (
    <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
        Distribución global del margen
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.8, mb: 1.5 }}>
        Mezcla histórica ponderada por kg. Sirve para ver estabilidad y cola negativa (si existe).
      </Typography>

      <Box sx={{ position: 'relative', height: 140, display: 'flex', alignItems: 'flex-end', gap: 0.6 }}>
        {counts.map((c, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: `${(c / maxCount) * 100}%`,
              borderRadius: 1,
              bgcolor: 'rgba(255,255,255,0.35)',
            }}
            title={`Bin ${i + 1}: ${c}`}
          />
        ))}

        {/* markers (vertical lines) */}
        {markers
          .filter((m) => typeof m.x === 'number')
          .map((m) => {
            const leftPct = (((m.x as number) - min) / span) * 100;
            return (
              <Box key={m.label} sx={{ position: 'absolute', left: `${leftPct}%`, top: 0, bottom: 0, width: 2, bgcolor: 'rgba(255,255,255,0.95)' }}>
                <Box sx={{ position: 'absolute', top: -22, left: -40, width: 120 }}>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {m.label}
                  </Typography>
                </Box>
              </Box>
            );
          })}
      </Box>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.2, opacity: 0.8 }}>
        <Typography variant="caption">{eurKg(min)} €/kg</Typography>
        <Typography variant="caption">{eurKg(max)} €/kg</Typography>
      </Stack>
    </Box>
  );
}

export default function MarginStatusView() {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [overviewRaw, setOverviewRaw] = useState<unknown>(null);
  const [globalRaw, setGlobalRaw] = useState<unknown>(null);

  // Fragilidad (qué pasa si...)
  const [fragilityRaw, setFragilityRaw] = useState<unknown>(null);

  // Drivers globales (ya existente)
  const [riskDriversRaw, setRiskDriversRaw] = useState<unknown>(null);

  // Drill-down: proveedor seleccionado para drivers
  const [selectedSupplierForDrivers, setSelectedSupplierForDrivers] = useState<string | null>(null);
  const [supplierRiskDriversRaw, setSupplierRiskDriversRaw] = useState<unknown>(null);
  const [supplierDriversLoading, setSupplierDriversLoading] = useState(false);
  const [supplierDriversError, setSupplierDriversError] = useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);

  // overlays seleccionados (proveedores)
  const [selectedSuppliers, setSelectedSuppliers] = useState<Record<string, boolean>>({});

  const overview = useMemo(() => extractOverview(overviewRaw as any), [overviewRaw]);
  const globalDist = useMemo(() => extractGlobalDist(globalRaw as any), [globalRaw]);
  const fragility = useMemo(() => extractFragility(fragilityRaw as any), [fragilityRaw]);
  const riskDrivers = useMemo(() => extractRiskDrivers(riskDriversRaw as any), [riskDriversRaw]);
  const supplierRiskDrivers = useMemo(() => extractRiskDrivers(supplierRiskDriversRaw as any), [supplierRiskDriversRaw]);

  const globalSummary = globalDist.global?.summary;

  const buffer =
    globalSummary?.mean != null && globalSummary?.p10 != null ? (globalSummary.mean as number) - (globalSummary.p10 as number) : null;

  // Headroom de compra (aprox.) a nivel global: baseline global vs max_price promedio ponderado por kg.
  const baselineGlobalPrice = overview.global?.avg_price_weighted_eur_per_kg ?? null;

  const maxPriceWeighted = useMemo(() => {
    const rows = overview.rows || [];
    let num = 0;
    let den = 0;

    for (const r of rows) {
      if (typeof r.max_price !== 'number') continue;
      const w = typeof r.total_kg === 'number' ? r.total_kg : 1;
      num += r.max_price * w;
      den += w;
    }
    return den > 0 ? num / den : null;
  }, [overview.rows]);

  const headroomPurchase =
    baselineGlobalPrice != null && maxPriceWeighted != null ? (maxPriceWeighted as number) - (baselineGlobalPrice as number) : null;

  const controlEval = computeControlState({
    decisionGrade: globalDist.quality?.decision_grade,
    probLoss: globalSummary?.probability_of_loss,
    buffer: buffer ?? undefined,
  });

  const toggleSupplier = (s: string) => {
    setSelectedSuppliers((prev) => ({ ...prev, [s]: !prev[s] }));
  };

  const handleRefresh = async () => {
    setErrorMsg(null);
    setIsLoading(true);
    try {
      const [o, g, f, d] = await Promise.all([
        getIndustrialRiskOverview({ n_sims: 1000, risk_target: 0.2, baseline: 'weighted_avg', max_suppliers: 20 }),
        getIndustrialRiskGlobalDistribution({ n_sims: 3000, bins: 30, baseline: 'weighted_avg', max_suppliers: 20 }),
        getIndustrialRiskFragility({
          scope: 'global',
          baseline: 'weighted_avg',
          n_sims: 6000,
          alpha: 0.10,
          random_seed: 7,
          shock_price_delta: 0.05,
          shock_yield_delta_abs: -0.01,
          h_price: 0.01,
          h_yield_abs: 0.005,
          loss_target: 0.10,
        }),
        getIndustrialRiskRiskDrivers({ scope: 'global', baseline: 'weighted_avg', n_sims: 4000, alpha: 0.10, random_seed: 7 }),
      ]);
      setOverviewRaw(o);
      setGlobalRaw(g);
      setFragilityRaw(f);
      setRiskDriversRaw(d);

      // Selección inicial: top 2 por kg (si no hay selección previa)
      if (!Object.keys(selectedSuppliers).length) {
        const top = (extractOverview(o as any).rows || []).slice(0, 2).map((r) => r.supplier);
        const init: Record<string, boolean> = {};
        top.forEach((s) => (init[s] = true));
        setSelectedSuppliers(init);
      }
    } catch (e: any) {
      setErrorMsg(e?.message ?? 'Error cargando Radar global.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedSupplierForDrivers) {
      setSupplierRiskDriversRaw(null);
      setSupplierDriversError(null);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setSupplierDriversLoading(true);
      setSupplierDriversError(null);
      try {
        const d = await getIndustrialRiskRiskDrivers({
          scope: 'supplier',
          supplier: selectedSupplierForDrivers,
          baseline: 'weighted_avg',
          n_sims: 4000,
          alpha: 0.10,
          random_seed: 7,
        });
        if (!cancelled) setSupplierRiskDriversRaw(d);
      } catch (e: any) {
        if (!cancelled) setSupplierDriversError(e?.message ?? 'Error cargando drivers del proveedor.');
      } finally {
        if (!cancelled) setSupplierDriversLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [selectedSupplierForDrivers]);

  const markers = useMemo(() => {
    const ms: { label: string; x?: number }[] = [];
    if (globalSummary?.mean != null) ms.push({ label: `Global (medio): ${eurKg(globalSummary.mean)} €/kg`, x: globalSummary.mean });

    const perSupplier = (globalDist.suppliers ?? []).filter((s) => selectedSuppliers[s.supplier]);
    for (const s of perSupplier) {
      if (s.summary?.mean != null) ms.push({ label: `${s.supplier}: ${eurKg(s.summary.mean)} €/kg`, x: s.summary.mean });
    }
    return ms.slice(0, 4); // evitamos saturación visual
  }, [globalDist.suppliers, globalSummary?.mean, selectedSuppliers]);

  const combinedPayload = useMemo(
    () => ({
      overview: overviewRaw,
      global_distribution: globalRaw,
      fragility: fragilityRaw,
      risk_drivers: riskDriversRaw,
      risk_drivers_supplier: selectedSupplierForDrivers ? { supplier: selectedSupplierForDrivers, payload: supplierRiskDriversRaw } : null,
    }),
    [overviewRaw, globalRaw, fragilityRaw, riskDriversRaw, selectedSupplierForDrivers, supplierRiskDriversRaw]
  );

  return (
    <Stack spacing={2.2}>
      <Stack spacing={0.6}>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          Radar global de margen
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Estado actual basado en histórico real. Comparativa por proveedor + distribución global (ponderada por kg).
        </Typography>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ sm: 'center' }}>
        <Button variant="contained" onClick={handleRefresh} disabled={isLoading}>
          {isLoading ? 'Actualizando…' : 'Actualizar radar'}
        </Button>
        <Button variant="outlined" onClick={() => setDrawerOpen(true)} disabled={!overviewRaw && !globalRaw}>
          Insights técnicos
        </Button>
        {globalDist.quality?.decision_grade === false ? (
          <Alert severity="warning" sx={{ ml: { sm: 1.5 }, flex: 1 }}>
            Datos no aptos para decisión ejecutiva (revisar calidad/cobertura).
          </Alert>
        ) : null}
      </Stack>

      {/* Control del margen (capa ejecutiva) */}
      <Box
        sx={{
          p: 2.2,
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.10)',
          bgcolor: 'rgba(255,255,255,0.05)',
        }}
      >
        <Stack spacing={1.2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Stack spacing={0.2}>
              <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                Control del margen
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Estado: <Chip size="small" label={controlEval.state} color={stateColor(controlEval.state) as any} />
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {controlEval.rationale}
              </Typography>
              {controlEval.state === 'REVISAR_DATOS' ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Antes de decidir:
                  </Typography>

                  {globalDist.quality?.reasons?.length ? (
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {globalDist.quality.reasons.slice(0, 3).map((r, idx) => (
                        <Box component="li" key={`${idx}-${r}`}>
                          <Typography variant="body2">{r}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2">
                      Faltan métricas o la cobertura no es suficiente para evaluar el control del margen con fiabilidad.
                    </Typography>
                  )}
                </Alert>
              ) : null}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                variant="outlined"
                label={`Riesgo de pérdida: ${
                  globalSummary?.probability_of_loss != null ? `${pct01(globalSummary.probability_of_loss)}%` : '—'
                }`}
              />
              <Chip variant="outlined" label={`Buffer: ${buffer != null ? `${eurKg(buffer)} €/kg` : '—'}`} />
              <Chip
                variant="outlined"
                label={`Zona adversa (razonable): ${globalSummary?.p10 != null ? `${eurKg(globalSummary.p10)} €/kg` : '—'}`}
              />
            </Stack>
          </Stack>

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <KpiCard
                title="Margen estructural (esperado)"
                value={globalSummary?.mean != null ? `${eurKg(globalSummary.mean)} €/kg` : '—'}
                subtitle="Beneficio por kg de materia prima, bajo mezcla histórica real"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <KpiCard
                title="Escenario adverso razonable"
                value={globalSummary?.p10 != null ? `${eurKg(globalSummary.p10)} €/kg` : '—'}
                subtitle="Zona baja realista del histórico (no es el peor caso)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <KpiCard
                title="Espacio de compra (global)"
                value={headroomPurchase != null ? `${eurKg(headroomPurchase)} €/kg` : '—'}
                subtitle="Cuánto podría subir el precio medio antes de entrar en el límite de riesgo (aprox.)"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Nota: el margen se expresa por kg de materia prima comprada (€/kg). El “espacio de compra” usa el límite por proveedor promediado y ponderado por
                kg (orientativo para comité, no contabilidad).
              </Typography>
            </Grid>
          </Grid>
        </Stack>
      </Box>

      {/* Qué pasa si… (Fragilidad económica) */}
      <Box
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.10)',
          bgcolor: 'rgba(255,255,255,0.03)',
        }}
      >
        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Qué pasa si…
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Impacto cuantificado de cambios pequeños y realistas sobre el margen (bajo incertidumbre histórica).
              </Typography>
            </Box>

            <Box>
              <KpiCard
                title="Baseline compra"
                value={fragility.baseline_price != null ? `${eurKg(fragility.baseline_price)} €/kg` : '—'}
                subtitle="Referencia para shocks"
              />
            </Box>
          </Stack>

          {fragility.quality?.decision_grade === false ? (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                REVISAR_DATOS
              </Typography>
              {fragility.quality.reasons?.length ? (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {fragility.quality.reasons.slice(0, 3).map((r, idx) => (
                    <Box component="li" key={`${idx}-${r}`}>
                      <Typography variant="body2">{r}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2">Los datos no son suficientes para cuantificar fragilidad.</Typography>
              )}
            </Alert>
          ) : null}

          <Grid container spacing={1.2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                  Compra +{fragility.shockPrice?.delta != null ? eurKg(fragility.shockPrice.delta) : '0.05'} €/kg
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  ES10: {fragility.shockPrice?.delta_es != null ? `${fragility.shockPrice.delta_es > 0 ? '+' : ''}${eurKg(fragility.shockPrice.delta_es)} €/kg` : '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Colchón: {fragility.shockPrice?.delta_buffer != null ? `${fragility.shockPrice.delta_buffer > 0 ? '+' : ''}${eurKg(fragility.shockPrice.delta_buffer)} €/kg` : '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  P(pérdida): {fragility.shockPrice?.delta_prob_loss != null ? `${fragility.shockPrice.delta_prob_loss > 0 ? '+' : ''}${pct01(fragility.shockPrice.delta_prob_loss)}%` : '—'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                  Yield {fragility.shockYield?.delta_abs != null ? `${fragility.shockYield.delta_abs > 0 ? '+' : ''}${(fragility.shockYield.delta_abs * 100).toFixed(1)}pp` : '-1.0pp'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  ES10: {fragility.shockYield?.delta_es != null ? `${fragility.shockYield.delta_es > 0 ? '+' : ''}${eurKg(fragility.shockYield.delta_es)} €/kg` : '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Colchón: {fragility.shockYield?.delta_buffer != null ? `${fragility.shockYield.delta_buffer > 0 ? '+' : ''}${eurKg(fragility.shockYield.delta_buffer)} €/kg` : '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  P(pérdida): {fragility.shockYield?.delta_prob_loss != null ? `${fragility.shockYield.delta_prob_loss > 0 ? '+' : ''}${pct01(fragility.shockYield.delta_prob_loss)}%` : '—'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                  Límite antes de zona de riesgo
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Target P(pérdida): {fragility.breakpoints?.loss_target != null ? `${pct01(fragility.breakpoints.loss_target)}%` : '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  ΔPrecio (€/kg) hasta target:{' '}
                  {typeof fragility.breakpoints?.purchase_price_delta_to_loss_target === 'number'
                    ? `+${eurKg(fragility.breakpoints.purchase_price_delta_to_loss_target)}`
                    : '—'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  ΔYield (pp) hasta target:{' '}
                  {typeof fragility.breakpoints?.yield_delta_abs_to_loss_target === 'number'
                    ? `${(fragility.breakpoints.yield_delta_abs_to_loss_target * 100).toFixed(1)}pp`
                    : '—'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            Nota: yield shock escala la producción (units) proporcionalmente para mantener coherencia física (sin inventar mix).
          </Typography>
        </Stack>
      </Box>

      {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}

      {/* Qué mueve el riesgo (drivers del downside) */}
      <Box
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.10)',
          bgcolor: 'rgba(255,255,255,0.03)',
        }}
      >
        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Qué mueve el riesgo (zona adversa)
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Atribución del downside (ES10) por palancas ejecutivas: compra, industrial, comercial y packaging.
              </Typography>
            </Box>

            <Box>
              <KpiCard
                title="ES10 (downside)"
                value={riskDrivers.metric?.value != null ? `${eurKg(riskDrivers.metric.value)} €/kg` : '—'}
                subtitle="Media del 10% peor de escenarios"
              />
            </Box>
          </Stack>

          {riskDrivers.quality?.decision_grade === false ? (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                REVISAR_DATOS
              </Typography>
              {riskDrivers.quality.reasons?.length ? (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {riskDrivers.quality.reasons.slice(0, 3).map((r, idx) => (
                    <Box component="li" key={`${idx}-${r}`}>
                      <Typography variant="body2">{r}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2">Los datos no son suficientes para una atribución robusta.</Typography>
              )}
            </Alert>
          ) : null}

          {riskDrivers.drivers?.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900 }}>Driver</TableCell>
                  <TableCell sx={{ fontWeight: 900 }} align="right">
                    Contribución al downside
                  </TableCell>
                  <TableCell sx={{ fontWeight: 900 }} align="right">
                    Efecto en ES10 (€/kg)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {riskDrivers.drivers.slice(0, 4).map((d) => (
                  <TableRow key={d.key}>
                    <TableCell>{d.label}</TableCell>
                    <TableCell align="right">{d.contribution_share != null ? `${(d.contribution_share * 100).toFixed(0)}%` : '—'}</TableCell>
                    <TableCell align="right">{d.shapley_effect_es_per_kg != null ? eurKg(d.shapley_effect_es_per_kg) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Aún no hay drivers disponibles (o están degradados por calidad de datos).
            </Typography>
          )}

          {/* --- INSERTAR AQUÍ EL BLOQUE DE PROVEEDOR --- */}

          {selectedSupplierForDrivers ? (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.08)',
                bgcolor: 'rgba(255,255,255,0.02)',
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Proveedor seleccionado: {selectedSupplierForDrivers}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ cursor: 'pointer', opacity: 0.7 }}
                    onClick={() => setSelectedSupplierForDrivers(null)}
                  >
                    Quitar selección
                  </Typography>
                </Stack>

                {supplierDriversLoading ? (
                  <Typography variant="body2">
                    Cargando drivers del proveedor...
                  </Typography>
                ) : supplierDriversError ? (
                  <Alert severity="error">{supplierDriversError}</Alert>
                ) : supplierRiskDrivers?.drivers?.length ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 900 }}>Driver</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900 }}>
                          Contribución al downside
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900 }}>
                          Efecto en ES10 (€/kg)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {supplierRiskDrivers.drivers.slice(0, 4).map((d) => (
                        <TableRow key={d.key}>
                          <TableCell>{d.label}</TableCell>
                          <TableCell align="right">
                            {d.contribution_share != null
                              ? `${(d.contribution_share * 100).toFixed(0)}%`
                              : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {d.shapley_effect_es_per_kg != null
                              ? eurKg(d.shapley_effect_es_per_kg)
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    No hay drivers disponibles para este proveedor.
                  </Typography>
                )}
              </Stack>
            </Box>
          ) : null}
          
        </Stack>
      </Box>      

      {/* Variabilidad y distribución (soporte al control) */}
      {globalSummary?.mean != null && globalSummary?.p10 != null && globalSummary?.p90 != null ? (
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              Variabilidad y distribución del margen
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Aquí se ve la dispersión real: cuánto “se mueve” el margen y dónde está la zona adversa razonable.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <RangeBar
              adverseValue={globalSummary.p10}
              expectedValue={globalSummary.mean}
              favorableValue={globalSummary.p90}
              unit="€/kg"
              formatValue={(n) => eurKg(n)}
              adverseLabel="Adverso"
              expectedLabel="Esperado"
              favorableLabel="Favorable"
            />
          </Grid>

          <Grid item xs={12}>
            <Histogram
              bins={globalDist.global?.histogram?.bins}
              counts={globalDist.global?.histogram?.counts}
              minX={globalSummary.min}
              maxX={globalSummary.max}
              markers={markers}
            />
          </Grid>

          {/* Selector overlays */}
          {globalDist.suppliers?.length ? (
            <Grid item xs={12}>
              <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  Comparar proveedores sobre la curva global
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 1.2 }}>
                  Activa 1–3 proveedores para ver su posición frente al global.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {globalDist.suppliers.map((s) => (
                    <Chip
                      key={s.supplier}
                      label={`${s.supplier}${s.summary?.mean != null ? ` · ${eurKg(s.summary.mean)} €/kg` : ''}`}
                      onClick={() => toggleSupplier(s.supplier)}
                      color={selectedSuppliers[s.supplier] ? 'primary' : 'default'}
                      variant={selectedSuppliers[s.supplier] ? 'filled' : 'outlined'}
                    />
                  ))}
                </Stack>
              </Box>
            </Grid>
          ) : null}

          {/* Ranking (overview) */}
          {overview.rows?.length ? (
            <Grid item xs={12}>
              <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 0.6 }}>
                  Ranking por proveedor
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 1.6 }}>
                  Margen esperado y riesgo estructural al precio base (media ponderada). Incluye límite de compra (precio máximo defendible).
                </Typography>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Proveedor</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Margen (€/kg)</TableCell>
                      <TableCell align="right">Riesgo pérdida</TableCell>
                      <TableCell align="right">Precio base</TableCell>
                      <TableCell align="right">Precio máx.</TableCell>
                      <TableCell align="right">Gap vs máx.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overview.rows.map((r) => (
                      <TableRow
                        key={r.supplier}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() =>
                          setSelectedSupplierForDrivers(
                            selectedSupplierForDrivers === r.supplier ? null : r.supplier
                          )
                        }
                      >
                        <TableCell sx={{ fontWeight: 800 }}>{r.supplier}</TableCell>
                        <TableCell>
                          <VerdictChip verdict={r.verdict} />
                        </TableCell>
                        <TableCell align="right">{r.mean != null ? `${eurKg(r.mean)} ` : '—'}</TableCell>
                        <TableCell align="right">{r.prob_loss != null ? `${pct01(r.prob_loss)}%` : '—'}</TableCell>
                        <TableCell align="right">{r.baseline_price != null ? `${eurKg(r.baseline_price)} ` : '—'}</TableCell>
                        <TableCell align="right">{r.max_price != null ? `${eurKg(r.max_price)} ` : '—'}</TableCell>
                        <TableCell align="right">
                          {r.gap_vs_max != null ? `${r.gap_vs_max > 0 ? '+' : ''}${eurKg(r.gap_vs_max)} ` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Typography variant="caption" sx={{ display: 'block', mt: 1.2, opacity: 0.75 }}>
                  “Gap vs máx.” &gt; 0 significa que el precio base está por encima del límite defendible (riesgo alto). &lt; 0 significa margen para pagar más.
                </Typography>
              </Box>
            </Grid>
          ) : null}
        </Grid>
      ) : (
        <Alert severity="info">Cargando radar…</Alert>
      )}

      <TechInsightsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} payload={combinedPayload} />
    </Stack>
  );
}